import type { ContentType, DayOfWeek, Schedule, ScreenTimeBudget } from '@kidshub/contracts';
import { computeRemaining, type LimitingFactor, timeOfDayToMinutes } from '@kidshub/policy';

/**
 * What a set of screen-time settings actually adds up to.
 *
 * The settings are not meaningful one at a time. A forty-minute video budget
 * inside a thirty-minute day is not forty minutes of video, and a twenty-minute
 * session limit inside a fifteen-minute allowed window never completes a
 * session. The design board makes this the heart of the screen for that reason:
 * the parent is shown the consequence, in plain language, before they save.
 *
 * Two different questions are answered here, and they are kept apart on purpose:
 *
 * - **"What stops the child first?"** is the playback question, and it is
 *   `SPEC.md §17.1`'s minimum. It is answered by `computeRemaining` from
 *   `@kidshub/policy` and never recomputed here — the whole value of that
 *   function is that the backend, the tablet and this screen cannot disagree.
 * - **"How does a day add up?"** is a projection for a parent reading a form.
 *   It is arithmetic over settings, not a decision about playback, and nothing
 *   enforces it. If it ever becomes something the product enforces, it belongs
 *   in `packages/policy` rather than here.
 */

export interface TypeAllowance {
  contentType: ContentType;
  /** What the parent set for this type. */
  configuredSeconds: number;
  /** What it is worth once the daily total is applied. */
  effectiveSeconds: number;
  /** True when the daily budget, not this setting, is the real ceiling. */
  isCappedByDailyTotal: boolean;
  /** How far a fresh session of this type can run, from `computeRemaining`. */
  firstSessionSeconds: number;
  /** Which limit stops it. Translated for the parent, never shown as a code. */
  firstSessionLimit: LimitingFactor;
}

export interface DayFit {
  day: DayOfWeek;
  /** Total permitted wall-clock time on this day. */
  allowedSeconds: number;
  /** Sessions the parent can expect, once the daily budget is applied. */
  sessions: number;
  /** How much of the daily budget the day's windows can actually deliver. */
  usableSeconds: number;
  isBudgetReachable: boolean;
}

export interface ScreenTimeProjection {
  dailyTotalSeconds: number;
  typeAllowances: TypeAllowance[];
  /** The sum of what the parent set per type, before any ceiling applies. */
  configuredTypeTotalSeconds: number;
  /** True when the type budgets promise more than the day can deliver. */
  isOverCommitted: boolean;
  scheduledDays: DayFit[];
  /** The scheduled day that delivers the least, or `null` when nothing is scheduled. */
  tightestDay: DayFit | null;
  /** False when at least one scheduled day cannot deliver the daily budget. */
  isBudgetReachableEveryDay: boolean;
}

const SECONDS_PER_MINUTE = 60;

const NOTHING_SPENT = { totalSeconds: 0, byContentType: {} } as const;

/**
 * How many sessions fit in a span, given that each is followed by a rest.
 *
 * The final session needs no rest after it, which is why the rest is added back
 * before dividing. Without that, a three-hour window fits four twenty-minute
 * sessions rather than five.
 */
export const sessionsWithinSpan = (
  spanSeconds: number,
  sessionSeconds: number,
  restSeconds: number,
): number => {
  if (spanSeconds <= 0 || sessionSeconds <= 0) {
    return 0;
  }
  return Math.floor((spanSeconds + restSeconds) / (sessionSeconds + restSeconds));
};

const windowSeconds = (start: string, end: string): number =>
  Math.max(timeOfDayToMinutes(end) - timeOfDayToMinutes(start), 0) * SECONDS_PER_MINUTE;

const fitDay = (
  day: DayOfWeek,
  allowedSeconds: number,
  budget: ScreenTimeBudget,
  sessionsThatFit: number,
): DayFit => {
  const deliverableSeconds = Math.min(
    budget.dailyTotalSeconds,
    sessionsThatFit * budget.maxSessionSeconds,
  );
  const sessionsNeeded =
    budget.maxSessionSeconds > 0
      ? Math.ceil(budget.dailyTotalSeconds / budget.maxSessionSeconds)
      : 0;

  return {
    day,
    allowedSeconds,
    // A parent wants the number they will see happen, which is bounded by the
    // budget as well as by the clock: three sessions of twenty minutes exhaust
    // an hour, however many more the afternoon would have held.
    sessions: Math.min(sessionsThatFit, sessionsNeeded),
    usableSeconds: deliverableSeconds,
    isBudgetReachable: deliverableSeconds >= budget.dailyTotalSeconds,
  };
};

const allowanceFor = (
  contentType: ContentType,
  configuredSeconds: number,
  budget: ScreenTimeBudget,
): TypeAllowance => {
  const firstSession = computeRemaining({
    budget,
    spent: NOTHING_SPENT,
    contentType,
    sessionElapsedSeconds: 0,
    // The schedule is reported separately, as a day-shaped question. Asking it
    // here would mix "how long until the window closes" into a figure the
    // parent reads as "how long a session runs".
    secondsUntilWindowEnd: null,
  });

  return {
    contentType,
    configuredSeconds,
    effectiveSeconds: Math.min(configuredSeconds, budget.dailyTotalSeconds),
    isCappedByDailyTotal: configuredSeconds > budget.dailyTotalSeconds,
    firstSessionSeconds: firstSession.remainingSeconds,
    firstSessionLimit: firstSession.limitingFactor,
  };
};

export const projectScreenTime = (input: {
  budget: ScreenTimeBudget;
  schedule: Schedule;
}): ScreenTimeProjection => {
  const { budget, schedule } = input;

  const typeAllowances = Object.entries(budget.perContentTypeSeconds)
    .filter(([, seconds]) => seconds !== undefined)
    .map(([contentType, seconds]) => allowanceFor(contentType as ContentType, seconds, budget));

  const configuredTypeTotalSeconds = typeAllowances.reduce(
    (total, allowance) => total + allowance.configuredSeconds,
    0,
  );

  const scheduledDays = Object.entries(schedule.windowsByDay)
    .filter(([, windows]) => windows !== undefined && windows.length > 0)
    .map(([day, windows]) => {
      const allowedSeconds = windows.reduce(
        (total, window) => total + windowSeconds(window.start, window.end),
        0,
      );
      const sessionsThatFit = windows.reduce(
        (total, window) =>
          total +
          sessionsWithinSpan(
            windowSeconds(window.start, window.end),
            budget.maxSessionSeconds,
            budget.restPeriodSeconds,
          ),
        0,
      );
      return fitDay(day as DayOfWeek, allowedSeconds, budget, sessionsThatFit);
    });

  const tightestDay = scheduledDays.reduce<DayFit | null>(
    (tightest, day) =>
      tightest === null || day.usableSeconds < tightest.usableSeconds ? day : tightest,
    null,
  );

  return {
    dailyTotalSeconds: budget.dailyTotalSeconds,
    typeAllowances,
    configuredTypeTotalSeconds,
    isOverCommitted:
      typeAllowances.length > 0 && configuredTypeTotalSeconds > budget.dailyTotalSeconds,
    scheduledDays,
    tightestDay,
    isBudgetReachableEveryDay: scheduledDays.every((day) => day.isBudgetReachable),
  };
};
